/**
 * Vendor portal — Orders list.
 * All orders for the current vendor with status filter.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/store/storeCart";

type OrderRow = {
  id: string;
  order_number: string;
  status: string;
  total_cents: number;
  buyer_id: string;
  created_at: string;
  shipped_at: string | null;
  delivered_at: string | null;
};

const STATUSES = [
  "all",
  "paid",
  "fulfilled",
  "shipped",
  "delivered",
  "partially_refunded",
  "refunded",
  "disputed",
  "cancelled",
] as const;

const VendorOrders = () => {
  const { vendor } = useVendor();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<(typeof STATUSES)[number]>("all");

  useEffect(() => {
    if (!vendor) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("store_orders")
        .select(
          "id, order_number, status, total_cents, buyer_id, created_at, shipped_at, delivered_at",
        )
        .eq("vendor_id", vendor.id)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setOrders((data as OrderRow[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [vendor]);

  const filtered = useMemo(
    () => (filter === "all" ? orders : orders.filter((o) => o.status === filter)),
    [orders, filter],
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Orders</h2>
        <p className="text-sm text-muted-foreground">Manage incoming orders and fulfillment.</p>
      </div>

      <Card className="p-3 flex flex-wrap gap-1">
        {STATUSES.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
            className="capitalize"
          >
            {s.replace(/_/g, " ")}
          </Button>
        ))}
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">No orders to show.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => (
            <Link key={o.id} to={`/store/vendor/orders/${o.id}`}>
              <Card className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium">{o.order_number}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="secondary" className="capitalize">
                      {o.status.replace(/_/g, " ")}
                    </Badge>
                    <span className="font-semibold">{formatPrice(o.total_cents)}</span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorOrders;
